// [uTheory](http://utheory.com) - Copyright (c) Gregory Ristow 2014
//
// ## Blank for Interactive Game
//
// Include description of object & useful info here, please!
import InteractivePrototype from './interactivePrototype.ts';
import * as Notation from '../notation.ts';
import SkillCalculator from '../uTheory.Skills/skillCalculator.ts';
import * as Skills from '../uTheory.Skills/skills.ts';
import QuestionGenerator from '../uTheory.Skills/questionGenerator';
import utils from '../utils.ts';

class NameNoteOnLineOrSpace extends InteractivePrototype {
  constructor(displayDiv, options) {
    super();
    this.targetSpeedFromSkills = NameNoteOnLineOrSpace.registry
      .map((r) => r.skills)
      .reduce((a, b) => a.concat(b));
    this.settings = {
      instructions: {
        ariaLive: 'alert',
        ariaLiveRegion: 'all',
      },
      editor: false,
      notation: false,
      piano: false,
      letters: {
        letterButtons: true,
        checkButton: false,
      },
      checkMark: {},
      player: {},
      progressTracker: true,
      oneTryOnly: false,
    };
    this.init(displayDiv, options);
  }

  init(displayDiv, options) {
    options.maxQuestions = options.maxQuestions || 15;
    this.skillCalculator = new SkillCalculator();

    // now call the prototype's init function:
    super.init(displayDiv, options);
  }

  generateQuestions() {
    this.questions = [];
    this.questionPrompt = this.options.questionPrompt;
    if (typeof this.questionPrompt === 'undefined') {
      const totalQ =
        this.options.drill !== true ? this.options.questions.length : this.options.maxQuestions;
      this.questionPrompt = `What note is <strong>{question}</strong> of the {question} clef?<br />#{question} of ${totalQ}`;
    }

    this.note = new Notation.Note();

    let questions;
    if (this.options.drill === true) {
      questions = this.generateRandomQuestions();
    } else {
      questions = this.options.questions.map(
        (q) => ({
          line: q,
          clef: this.options.clef,
        }),
        this
      );
    }
    this.questions = questions.map((question, i) => {
      const clef = Notation.Clef.fromStringOrClef(question.clef);
      const note = clef.getNoteAtLine(question.line);
      const text = [
        `${clef.toInOrOnString(note)} the ${clef.toLineSpaceString(note)}`,
        clef,
        i + 1,
      ];
      const skill = this.skillCalculator.getClefSkill(note, clef);

      return {
        line: question.line,
        text,
        note,
        skills: [skill],
      };
    }, this);
    this.maxQuestions = this.questions.length;
  }

  addListeners() {
    this.letterPanel.addLetterCallback(this.check, this);
  }

  generateRandomQuestions() {
    this.options.clefs = this.options.clefs || (this.options.clef ? [this.options.clef] : false);
    const findSkill = (string) =>
      !!this.options.skills.find((skill) => Skills.makeString(skill).search(string) !== -1);
    if (!this.options.clefs) {
      this.options.clefs = [];
      if (findSkill('treble')) {
        this.options.clefs.push('treble');
      }
      if (findSkill('bass')) {
        this.options.clefs.push('bass');
      }
    }

    const generator = new QuestionGenerator();
    if (typeof this.options.maxQuestions === 'undefined') {
      this.options.maxQuestions = 15;
    }

    this.options.skills = this.options.skills.filter(
      (skill) => Skills.makeString(skill) !== 'lines_spaces'
    );

    return generator
      .multipleSkillsGenerate(this.options.skills, this.options.maxQuestions, this.options)
      .map((note) => {
        const clefString = note.prefersClef(utils.shuffle(this.options.clefs), 3)[0];
        console.log({ clefString, clefs: this.options.clefs, note: note.toString() });
        const clef = new Notation.Clef(clefString);

        return {
          line: clef.getLine(note),
          clef: clefString,
        };
      }, this);
  }

  check(letter) {
    if (this.rightAnswerDelay) return;
    if (this.optionsTrue('testMode')) {
      this.checkpointAnswer(letter);
      return;
    }
    if (this.questions[this.questionNumber].note.letter === letter) {
      this.rightAnswer(800);
    } else {
      this.wrongAnswer();
    }
  }

  // ## CheckPoint Mode Questions:
  newCheckpointQuestion(questionString) {
    return this.loadingPromise.then(() => {
      this.question = questionString;
      const questionParts = questionString.split('/');
      const note = new Notation.Note(questionParts[0]);
      const clef = new Notation.Clef(questionParts[1].toLowerCase());
      this.note = note;
      this.clef = clef;

      const questionText = `What note is ${clef.toInOrOnString(
        note
      )} the <strong>${clef.toLineSpaceString(
        note
      )}</strong> of the <strong>${clef.getName()}</strong> clef?`;

      this.instructionsDiv.innerHTML = questionText;

      this.checkpointQuestion = {
        note: this.note,
      };
      this.resize();
      return Promise.resolve(this.currentQuestion);
    });
  }

  getAnswerAndData(letter) {
    const correct = this.note.letter === letter;
    const skill = [this.skillCalculator.getClefSkill(this.note, this.clef)];

    const answerAndData = {
      question: this.question,
      questionType: 'NameNoteOnLineOrSpace',
      questionText: this.instructionsDiv.innerHTML,
      questionSVG: null,
      studentAnswerText: letter,
      studentAnswerSVG: null,
      correctAnswerText: this.note.letter,
      rightAnswers: correct ? 1 : 0,
      wrongAnswers: correct ? 0 : 1,
      skills: skill,
    };

    return answerAndData;
  }

  gameOver() {
    super.gameOver();
    this.instructionsDiv.innerHTML = 'Nice work!';
    this.instructionsDiv.setAttribute('role', 'alert');
    this.displayDiv.removeChild(this.lettersDiv);
  }

  playAnswer() {
    this.player.play(this.questions[this.questionNumber].note.getMidiNumber(), {
      startTime: 0,
      stopTime: 1,
    });
  }
}

NameNoteOnLineOrSpace.registry = [
  {
    interactive: 'NameNoteOnLineOrSpace',
    description: 'Line and Space Note Naming',
    skills: ['treble_c_g', 'treble_a_g', 'treble_high_legers', 'treble_low_legers'],
    alsoTrains: ['lines_spaces'],
    options: {
      clef: 'treble',
    },
  },
  {
    interactive: 'NameNoteOnLineOrSpace',
    description: 'Line and Space Note Naming',
    skills: ['bass_lines', 'bass_spaces', 'bass_high_legers', 'bass_low_legers'],
    alsoTrains: ['lines_spaces'],
    options: {
      clef: 'bass',
    },
  },
];

export default NameNoteOnLineOrSpace;
